import React from "react";
import Button from "../../components/staff/Button";
import DDMultiple, {
  type Option,
} from "../../components/Atomos/DropDown/DDMultiple";
import type { StaffContactOption } from "../../utils/fetchStaffContactOptions";
import {
  resolveBillingEmailAddressing,
  type BillingSendRecipients,
} from "../../utils/billingSendRecipients";

interface SendBillingDocModalProps {
  isOpen: boolean;
  docType: "invoice" | "payment";
  isTest: boolean;
  sendRecipients?: BillingSendRecipients | null;
  sendRecipientsLoading?: boolean;
  staffContacts?: StaffContactOption[];
  staffContactsLoading?: boolean;
  selectedStaff?: Option[];
  onSelectedStaffChange?: (selected: Option[]) => void;
  isSubmitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function SendBillingDocModal({
  isOpen,
  docType,
  isTest,
  sendRecipients = null,
  sendRecipientsLoading = false,
  staffContacts = [],
  staffContactsLoading = false,
  selectedStaff = [],
  onSelectedStaffChange,
  isSubmitting = false,
  onCancel,
  onConfirm,
}: SendBillingDocModalProps): React.ReactElement | null {
  if (!isOpen) {
    return null;
  }

  const docLabel = docType === "invoice" ? "invoice" : "payment receipt";
  const title = isTest ? "Send Test Email" : "Confirm Send";
  const confirmButtonText = isSubmitting ? "Sending..." : "Send";

  const staffDropdownOptions: Option[] = staffContacts.map((contact) => ({
    id: contact.id,
    name: contact.name,
  }));

  const selectedStaffEmails = selectedStaff
    .map(
      (option) =>
        staffContacts.find((contact) => contact.id === option.id)?.email ?? "",
    )
    .filter(Boolean);

  const canConfirmTest =
    !isTest || (!staffContactsLoading && selectedStaffEmails.length > 0);

  const addressing = sendRecipients
    ? resolveBillingEmailAddressing(sendRecipients)
    : null;
  const addressedToAccounting = Boolean(
    sendRecipients?.accountingEmail?.trim(),
  );

  const canConfirmNormal =
    isTest ||
    (!sendRecipientsLoading && Boolean(addressing?.toEmail));

  const formatContactEmailLine = (
    email: string | undefined,
    inherited?: boolean,
    jurisdictionName?: string,
  ): string => {
    const trimmed = String(email ?? "").trim();
    if (!trimmed) {
      return "—";
    }
    if (inherited && jurisdictionName?.trim()) {
      return `${trimmed} (from ${jurisdictionName.trim()})`;
    }
    return trimmed;
  };

  return (
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-80"
        onClick={isSubmitting ? undefined : onCancel}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="mb-4 font-decimal text-xl font-medium text-Default">
            {title}
          </h2>

          {isTest ? (
            <>
              <p className="mb-4 text-gray-font">
                Send a test {docLabel} email to selected WSG staff.
              </p>
              <div className="mb-6">
                <DDMultiple
                  title="Staff recipients"
                  objects={staffDropdownOptions}
                  selectedObjst={selectedStaff}
                  seter={(value) => {
                    const next =
                      typeof value === "function" ? value(selectedStaff) : value;
                    onSelectedStaffChange?.(next);
                  }}
                  disabled={isSubmitting || staffContactsLoading}
                  placeholder={
                    staffContactsLoading
                      ? "Loading staff..."
                      : "Search and select staff"
                  }
                  internalSearch
                  bulkActions
                  onBulkSelectAll={() =>
                    onSelectedStaffChange?.(staffDropdownOptions)
                  }
                  onBulkClearAll={() => onSelectedStaffChange?.([])}
                />

                {selectedStaffEmails.length > 0 && (
                  <div className="mt-3 rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-font">
                    <div className="font-semibold">Will be sent to:</div>
                    <ul className="mt-1 list-disc pl-5">
                      {selectedStaffEmails.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {!staffContactsLoading && staffContacts.length === 0 && (
                  <p className="mt-2 text-sm text-amber-700">
                    No staff contacts with email were found.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="mb-6 space-y-4 text-sm text-gray-font">
              {sendRecipientsLoading ? (
                <p>Loading recipients...</p>
              ) : (
                <>
                  <p>
                    You&apos;re about to send the {docLabel}.
                  </p>

                  {sendRecipients?.inheritNotice ? (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950">
                      <div className="font-semibold">Note</div>
                      <p className="mt-1">{sendRecipients.inheritNotice}</p>
                    </div>
                  ) : null}

                  <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3">
                    <div>
                      <span className="font-semibold">To:</span>{" "}
                      {addressing?.toEmail
                        ? `${formatContactEmailLine(addressing.toEmail)} (${
                            addressedToAccounting
                              ? "Accounting Contact"
                              : "Primary Contact"
                          })`
                        : "—"}
                    </div>
                    <div className="mt-2">
                      <span className="font-semibold">CC:</span>{" "}
                      {addressing?.ccEmail
                        ? `${formatContactEmailLine(addressing.ccEmail)} (${
                            addressing.ccRole === "secondary"
                              ? "Secondary Contact"
                              : "CC"
                          })`
                        : "—"}
                    </div>
                  </div>

                  {sendRecipients?.primaryAndAccountingSamePerson ? (
                    <p className="text-amber-800">
                      Primary and accounting contact are the same person
                      {sendRecipients.primaryName
                        ? `: ${sendRecipients.primaryName}`
                        : ""}
                      .
                    </p>
                  ) : null}

                  {!addressing?.toEmail ? (
                    <p className="text-amber-800">
                      No accounting or primary contact email assigned.
                    </p>
                  ) : null}

                  {!sendRecipients?.accountingEmail?.trim() ? (
                    <p className="text-amber-800">
                      No accounting contact defined.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button
              text="Cancel"
              color="secondary"
              variant="outlined"
              size="fixed"
              onClick={onCancel}
              disabled={isSubmitting}
            />
            <Button
              text={confirmButtonText}
              color="secondary"
              variant="filled"
              size="fixed"
              onClick={onConfirm}
              disabled={
                isSubmitting || !canConfirmTest || !canConfirmNormal
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
